import { useEffect, useMemo, useState } from "react";
import { useTenantData } from "./useTenantData";
import {
  getStudioClock,
  setRuntimeTenantTimeZone,
  type StudioClock,
} from "@/lib/utils/date/studio-clock";

/** Studio IANA zone from account settings; null falls back to the browser clock. */
export function useTenantTimeZone(): string | null {
  const { tenantData } = useTenantData();
  const timeZone = tenantData?.timeZone ?? null;
  setRuntimeTenantTimeZone(timeZone);
  return timeZone;
}

/** Civil date and wall clock in the tenant time zone. */
export function useStudioClock(now?: Date): StudioClock {
  const timeZone = useTenantTimeZone();
  return useMemo(
    () => getStudioClock(now ?? new Date(), timeZone),
    [now, timeZone],
  );
}

/** Advances on the minute so a live studio clock stays accurate without busy polling. */
export function useTickingNow(): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let intervalId = 0;
    const msToNextMinute =
      60_000 - (Date.now() % 60_000);

    const timeoutId = window.setTimeout(() => {
      setNow(new Date());
      intervalId = window.setInterval(() => setNow(new Date()), 60_000);
    }, Math.max(msToNextMinute, 250));

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, []);

  return now;
}
